import React from 'react';

export interface ILeaseRecordsApiError {
    title: string;
    fieldErrors: Record<string, string[]>;
    code?: string;
    supportId?: string;
}

const GENERIC_ERROR = 'The Lease Records request could not be completed. Please try again.';

const nonEmptyString = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

const normalizeErrors = (value: unknown): Record<string, string[]> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {};
    }

    return Object.keys(value as Record<string, unknown>).reduce((result, field) => {
        const messages = (value as Record<string, unknown>)[field];
        const normalized = (Array.isArray(messages) ? messages : [messages]).map(nonEmptyString).filter((message): message is string => !!message);
        if (normalized.length) {
            result[field] = normalized;
        }
        return result;
    }, {} as Record<string, string[]>);
};

export const parseLeaseRecordsApiError = (payload: unknown, status?: number): ILeaseRecordsApiError => {
    const body = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : undefined;
    const effectiveStatus = status || (typeof body?.status === 'number' ? body.status : undefined);
    const supportId = nonEmptyString(body?.supportId);
    // Never display an arbitrary legacy 5xx body; older endpoints can return SQL
    // and exception text. Ticket 1 ProblemDetails uses this same safe fallback.
    if (effectiveStatus && effectiveStatus >= 500) {
        return { title: GENERIC_ERROR, fieldErrors: {}, ...(supportId ? { supportId } : {}) };
    }

    const fieldErrors = normalizeErrors(body?.errors);
    const title =
        nonEmptyString(body?.title) ||
        nonEmptyString(body?.message) ||
        nonEmptyString(payload) ||
        Object.keys(fieldErrors)
            .reduce((messages, field) => messages.concat(fieldErrors[field]), [] as string[])
            .find(Boolean) ||
        GENERIC_ERROR;
    const code = nonEmptyString(body?.code);

    return { title, fieldErrors, ...(code ? { code } : {}), ...(supportId ? { supportId } : {}) };
};

export const getLeaseRecordsApiErrorMessage = (payload: unknown, status?: number): string => parseLeaseRecordsApiError(payload, status).title;

const normalizeField = (field: string): string => field.replace(/^request\./i, '').toLowerCase();
const fieldsMatch = (left: string, right: string): boolean => {
    const normalizedLeft = normalizeField(left);
    const normalizedRight = normalizeField(right);
    return normalizedLeft === normalizedRight || normalizedLeft.endsWith(`.${normalizedRight}`) || normalizedRight.endsWith(`.${normalizedLeft}`);
};

export const getLeaseRecordsFieldErrors = (error: ILeaseRecordsApiError | null, ...fields: string[]): string[] => {
    if (!error) {
        return [];
    }
    return Object.keys(error.fieldErrors).reduce(
        (messages, field) => (fields.some((requested) => fieldsMatch(field, requested)) ? messages.concat(error.fieldErrors[field]) : messages),
        [] as string[]
    );
};

export const clearLeaseRecordsFieldError = (error: ILeaseRecordsApiError | null, field: string): ILeaseRecordsApiError | null => {
    if (!error) {
        return null;
    }
    const fieldErrors = Object.keys(error.fieldErrors).reduce((result, key) => {
        if (!fieldsMatch(key, field)) {
            result[key] = error.fieldErrors[key];
        }
        return result;
    }, {} as Record<string, string[]>);
    return { ...error, fieldErrors };
};

export const LeaseRecordsErrorAlert: React.FC<{ error: ILeaseRecordsApiError | null; onDismiss?: () => void }> = ({ error, onDismiss }) => {
    if (!error) {
        return null;
    }
    return (
        <div role="alert" aria-live="assertive" style={alertStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <strong>{error.title}</strong>
                {onDismiss && (
                    <button type="button" onClick={onDismiss} aria-label="Dismiss error" style={dismissStyle}>
                        ×
                    </button>
                )}
            </div>
            {error.supportId && <div style={supportIdStyle}>Support ID: {error.supportId}</div>}
        </div>
    );
};

export const LeaseRecordsFieldError: React.FC<{ id: string; messages: string[] }> = ({ id, messages }) =>
    messages.length ? (
        <span id={id} role="alert" style={fieldErrorStyle}>
            {messages.join(' ')}
        </span>
    ) : null;

const alertStyle: React.CSSProperties = {
    margin: '0.75rem 0',
    padding: '0.75rem 1rem',
    color: '#991B1B',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: '4px',
};
const dismissStyle: React.CSSProperties = { border: 0, background: 'transparent', color: '#991B1B', cursor: 'pointer', fontSize: '1.1rem' };
const fieldErrorStyle: React.CSSProperties = { display: 'block', marginTop: '0.25rem', color: '#B91C1C', fontSize: '0.8rem' };
const supportIdStyle: React.CSSProperties = { marginTop: '0.35rem', fontSize: '0.8rem' };



// WEBPACK FOOTER //
// ./src/components/leases/LeaseRecordsApiError.tsx